import React from 'react';
import { Alert, Button, Spin, Tag, Tooltip } from 'antd';
import { ReloadOutlined, RobotOutlined } from '@ant-design/icons';
import CallAnalysisReport from './analysis/CallAnalysisReport';
import { toJalali } from '../../utils/dateUtils';

export const AI_STATUS_META = {
  none: { label: 'بدون خلاصه', color: 'default' },
  pending: { label: 'در صف خلاصه‌سازی', color: 'processing' },
  processing: { label: 'در حال خلاصه‌سازی', color: 'processing' },
  completed: { label: 'خلاصه‌شده', color: 'success' },
  failed: { label: 'خطا در خلاصه‌سازی', color: 'error' },
};

export const normalizeAiStatus = (status) => String(status || 'none').toLowerCase();

export const isAiInProgress = (status) => {
  const s = normalizeAiStatus(status);
  return s === 'pending' || s === 'processing';
};

export const SENTIMENT_META = {
  positive: { label: 'مثبت', color: 'success' },
  neutral: { label: 'خنثی', color: 'default' },
  negative: { label: 'منفی', color: 'error' },
};

/** برچسب‌های فشرده تحلیل ساختاریافته (احساس مشتری و نیاز به پیگیری) */
export const AiAnalysisTags = ({ analysis }) => {
  if (!analysis) return null;
  const sentiment = SENTIMENT_META[analysis.sentiment];
  const hasFollowUp = typeof analysis.follow_up_needed === 'boolean';
  if (!sentiment && !hasFollowUp) return null;

  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {sentiment && (
        <Tag color={sentiment.color} className="custom-font m-0 text-xs">احساس: {sentiment.label}</Tag>
      )}
      {hasFollowUp && (
        <Tag color={analysis.follow_up_needed ? 'warning' : 'default'} className="custom-font m-0 text-xs">
          {analysis.follow_up_needed ? 'نیاز به پیگیری' : 'بدون پیگیری'}
        </Tag>
      )}
    </span>
  );
};

/** کلیدهای قالب «JSON با فیلدهای آماده»؛ هر کلید دیگری یعنی ساختار سفارشی تعریف‌شده در پرامپت */
const BUILTIN_ANALYSIS_KEYS = new Set(['summary', 'customer_request', 'outcome', 'sentiment', 'follow_up_needed', 'topics']);

const isBuiltinAnalysis = (analysis) => Object.keys(analysis).every((key) => BUILTIN_ANALYSIS_KEYS.has(key));

/** تماس دارای گزارش تحلیلی کامل (خروجی JSON سفارشی) است */
export const hasAnalysisReport = (call) => Boolean(
  call?.ai_analysis
  && typeof call.ai_analysis === 'object'
  && !isAiInProgress(call.ai_status)
  && !isBuiltinAnalysis(call.ai_analysis),
);

const AnalysisRow = ({ label, children }) => (
  <div className="flex items-start gap-2 text-sm">
    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0 min-w-[110px] leading-6">{label}:</span>
    <span className="text-gray-800 dark:text-gray-100 break-words leading-6">{children}</span>
  </div>
);

/** فقط تماس‌هایی که رونویسی آن‌ها موفق بوده قابل خلاصه‌سازی هستند */
export const canSummarizeCall = (call) =>
  Boolean(call) && (call.can_summarize ?? call.transcribe_status === 'completed');

/**
 * نمایش وضعیت و متن خلاصه هوش مصنوعی یک تماس به همراه دکمه خلاصه‌سازی / تحلیل مجدد.
 */
const CallAiSummaryPanel = ({ call, summarizing = false, onSummarize, variant = 'full', onOpenFullReport }) => {
  if (!call) return null;

  const status = normalizeAiStatus(call.ai_status);
  const inProgress = isAiInProgress(status);
  const summarizable = canSummarizeCall(call);
  const analysis = call.ai_analysis && typeof call.ai_analysis === 'object' ? call.ai_analysis : null;

  const actionButton = summarizable && onSummarize && !inProgress ? (
    <Button
      size="small"
      type={call.summary ? 'default' : 'primary'}
      icon={call.summary ? <ReloadOutlined /> : <RobotOutlined />}
      loading={summarizing}
      onClick={() => onSummarize(call.id)}
      className="custom-font text-xs"
    >
      {call.summary ? 'تحلیل مجدد' : 'خلاصه‌سازی با هوش مصنوعی'}
    </Button>
  ) : null;

  // خروجی JSON سفارشی: گزارش تحلیلی کامل جایگزین کادر خلاصه می‌شود
  if (hasAnalysisReport(call)) {
    return (
      <CallAnalysisReport
        analysis={analysis}
        call={call}
        variant={variant}
        onOpenFullReport={onOpenFullReport}
        action={actionButton}
      />
    );
  }

  return (
    <div className="custom-font space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 font-medium text-gray-700 dark:text-gray-200">
          <RobotOutlined className="text-purple-600" />
          <span>خلاصه مکالمه (هوش مصنوعی)</span>
          {status !== 'none' && (
            <Tag color={AI_STATUS_META[status]?.color} className="custom-font m-0 text-xs">
              {AI_STATUS_META[status]?.label}
            </Tag>
          )}
        </div>
        {actionButton}
      </div>

      {inProgress ? (
        <div className="p-6 text-center rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-950/20">
          <Spin tip={<span className="custom-font text-xs">در حال تحلیل متن مکالمه با هوش مصنوعی...</span>}>
            <div className="h-8" />
          </Spin>
        </div>
      ) : (
        <>
          {status === 'failed' && (
            <Alert
              type="error"
              showIcon
              className="custom-font"
              message={<span className="custom-font">خلاصه‌سازی ناموفق بود</span>}
              description={<span className="custom-font text-xs break-words">{call.ai_error || 'پردازش با خطا مواجه شد.'}</span>}
            />
          )}

          {call.summary ? (
            <div className="p-3.5 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20">
              <p className="m-0 text-sm leading-7 text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words">
                {call.summary}
              </p>
              {analysis && isBuiltinAnalysis(analysis) && (
                <div className="mt-3 pt-3 border-t border-purple-200/70 dark:border-purple-800/70 space-y-1.5">
                  <AiAnalysisTags analysis={analysis} />
                  {analysis.customer_request && <AnalysisRow label="درخواست مشتری">{analysis.customer_request}</AnalysisRow>}
                  {analysis.outcome && <AnalysisRow label="نتیجه تماس">{analysis.outcome}</AnalysisRow>}
                  {Array.isArray(analysis.topics) && analysis.topics.length > 0 && (
                    <AnalysisRow label="موضوعات">
                      <span className="inline-flex flex-wrap gap-1">
                        {analysis.topics.map((topic) => (
                          <Tag key={topic} className="custom-font m-0 text-xs">{topic}</Tag>
                        ))}
                      </span>
                    </AnalysisRow>
                  )}
                </div>
              )}
              {(call.ai_model || call.ai_summarized_at) && (
                <div className="flex items-center gap-2 flex-wrap mt-2 text-xs text-gray-400">
                  {call.ai_model && (
                    <Tag color="purple" className="custom-font m-0 text-xs" dir="ltr">{call.ai_model}</Tag>
                  )}
                  {call.ai_summarized_at && (
                    <Tooltip title="زمان تولید خلاصه">
                      <span>{toJalali(call.ai_summarized_at, 'jYYYY/jMM/jDD HH:mm')}</span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          ) : status !== 'failed' && (
            <div className="p-4 text-center text-xs text-gray-500 rounded-lg border border-gray-100 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40">
              {summarizable
                ? 'هنوز خلاصه‌ای برای این مکالمه ایجاد نشده است.'
                : 'خلاصه‌سازی فقط برای مکالماتی که با موفقیت رونویسی شده‌اند امکان‌پذیر است.'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CallAiSummaryPanel;
